import { useEffect, useState } from 'react';
import { useAudienceStore } from '../stores/audience';
import {
  AUDIENCES,
  getAudienceById,
  getAudienceByPath,
  type AudienceId,
  type AudienceMeta,
} from '@shared/constants/audiences';

/**
 * Retourne l'AudienceMeta active selon une priorité claire et stable :
 *
 *   1. **URL** — si le pathname matche une page d'audience (`/particuliers`, `/artisans`…).
 *      C'est la source de vérité primaire car elle est observable côté SSR et survit
 *      au rafraîchissement.
 *   2. **Store Zustand** — sinon, le profil stocké en localStorage (+ miroir cookie).
 *      Sert pour les pages "hors audience" (home, outils, blog…) où on veut quand même
 *      personnaliser le contenu selon le profil sélectionné.
 *
 * Côté SSR (window indisponible) : retourne `null` pour éviter tout hydration mismatch.
 * Au montage, le composant re-render avec la bonne valeur.
 *
 * Réagit automatiquement à :
 * - changements de `audienceId` dans le store (sélection via Dropdown/Modal)
 * - changements d'URL (navigation Vike / popstate)
 */
export function useActiveAudience(): AudienceMeta | null {
  const audienceIdFromStore = useAudienceStore((s) => s.audienceId);
  const hydrate = useAudienceStore((s) => s.hydrate);
  const [pathname, setPathname] = useState<string | null>(null);

  useEffect(() => {
    hydrate();
    setPathname(window.location.pathname);
    const onPop = () => setPathname(window.location.pathname);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [hydrate]);

  if (pathname === null) return null;

  const fromUrl = getAudienceByPath(pathname);
  if (fromUrl) return fromUrl;

  if (audienceIdFromStore) {
    return getAudienceById(audienceIdFromStore) ?? null;
  }

  return null;
}

/**
 * Variante qui ignore l'URL et retourne UNIQUEMENT le profil stocké.
 *
 * Utile pour les pages "hors audience" (home, outils, blog) où on veut un état
 * réactif au choix du profil sans dépendre du pathname.
 *
 * Note : retourne `null` côté SSR jusqu'à hydratation pour éviter les mismatchs.
 */
export function useStoredAudienceId(): AudienceId | null {
  const audienceId = useAudienceStore((s) => s.audienceId);
  const hydrated = useAudienceStore((s) => s.hydrated);
  const hydrate = useAudienceStore((s) => s.hydrate);

  useEffect(() => {
    hydrate();
  }, [hydrate]);

  if (!hydrated) return null;
  return audienceId;
}

/** Variante qui retourne l'AudienceMeta complète stockée (ou null). */
export function useStoredAudience(): AudienceMeta | null {
  const id = useStoredAudienceId();
  return id ? (AUDIENCES.find((a) => a.id === id) ?? null) : null;
}
